import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, X, Plus, FlaskConical, GalleryHorizontalEnd, Megaphone, Package, BookOpen, Wand2 } from "lucide-react";
import { CtaButton, SecondaryButton, MonoLabel } from "../components/kit";
import { AddToCalendar } from "../components/AddToCalendar";
import { PILLAR_COLORS } from "../components/EditorialCalendar";
import api, { formatApiErrorDetail } from "../lib/api";

const pillarColor = (name, pillars) => {
  const i = (pillars || []).indexOf(name);
  return i === -1 ? "neutral" : PILLAR_COLORS[i % PILLAR_COLORS.length];
};
const ARCHETYPE_BY_TIPO = { prodotto: "product", processo: "steps", educativo: "listicle" };

const PillarDot = ({ color, className = "" }) => (
  <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${className}`} style={{ background: `var(--${color}-fg)` }} />
);

const TIPO_ICONS = { prodotto: Package };

export default function EditorialPlan() {
  const navigate = useNavigate();
  const [brain, setBrain] = useState(null);
  const [pillars, setPillars] = useState([]);
  const [pillarInput, setPillarInput] = useState("");
  const [editingPillar, setEditingPillar] = useState(null);
  const [focus, setFocus] = useState("");
  const [proposing, setProposing] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const [plan, setPlan] = useState(null); // latest history doc kind=plan
  const [calendarSlots, setCalendarSlots] = useState([]);
  const [addingIdx, setAddingIdx] = useState(null);
  const proposedRef = useRef(false);
  const pollRef = useRef(null);

  const loadCalendar = () => api.get("/calendar").then(({ data }) => setCalendarSlots(data.slots || [])).catch(() => {});
  const loadLatestPlan = () =>
    api.get("/script-lab/history").then(({ data }) => setPlan(data.find((h) => h.kind === "plan") || null)).catch(() => {});

  useEffect(() => {
    api.get("/brand-brain").then(({ data }) => { setBrain(data); setPillars(data?.content_pillars || []); }).catch(() => {});
    loadLatestPlan();
    loadCalendar();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const savePillars = async (next) => {
    const prev = pillars;
    setPillars(next);
    try {
      const { data } = await api.put("/brand-brain/pillars", { pillars: next });
      setPillars(data.pillars);
    } catch {
      setPillars(prev);
      toast.error("Couldn't save pillars. Try again.");
    }
  };

  const proposePillars = async () => {
    setProposing(true);
    try {
      const { data } = await api.post("/pillars/propose");
      await savePillars(data.pillars);
      toast("Pillars proposed from your Brand Brain.", { description: "Tweak them freely — they're yours." });
    } catch {
      toast.error("Couldn't propose pillars — add them manually or retry.");
    } finally {
      setProposing(false);
    }
  };

  useEffect(() => {
    if (brain && !(brain.content_pillars || []).length && !proposedRef.current) {
      proposedRef.current = true;
      proposePillars();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brain]);

  const generatePlan = async () => {
    setPhase("generating");
    setErrMsg("");
    try {
      await api.post("/script-lab/plan", { mode: "prodotto", language: "auto", fields: { cliente: brain?.business_name || "" }, ...(focus.trim() ? { focus: focus.trim() } : {}) });
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        if (++ticks > 200) { clearInterval(pollRef.current); setPhase("error"); setErrMsg("Generation timed out. Try again."); return; }
        try {
          const { data } = await api.get("/script-lab/status");
          if (data.status === "failed") {
            clearInterval(pollRef.current);
            setPhase("error");
            setErrMsg(data.error || "Something broke on our side — try again.");
          } else if (data.status === "complete") {
            clearInterval(pollRef.current);
            await loadLatestPlan();
            setPhase("idle");
            toast("10 ideas ready.", { description: "They stay here — add to the calendar only what you want." });
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setPhase("error");
      setErrMsg(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const originOf = (idx) => `${plan.id}:${idx}`;
  const slotFor = (idx) => calendarSlots.find((s) => s.origin === originOf(idx));

  const addIdea = async (idea, idx, date) => {
    setAddingIdx(idx);
    try {
      const { data: slot } = await api.post("/calendar/slots", {
        title: idea.titolo, date, tipo: idea.tipo, punti: idea.punti, cta: idea.cta,
        pillar: idea.pillar || null, status: "idea", origin: originOf(idx),
      });
      loadCalendar();
      toast("Added to calendar.", { description: "Writing the caption…" });
      try {
        await api.post(`/calendar/slots/${slot.id}/caption`);
        loadCalendar();
        toast("Caption ready.", { description: "Find it on the calendar slot — editable and copy-ready." });
      } catch {
        toast.error("Added, but the caption failed — write it from the calendar slot.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add to calendar.");
      loadCalendar();
    } finally {
      setAddingIdx(null);
    }
  };

  const toScript = (idea) => navigate("/app/organic/scripts", { state: { scriptPrefill: { idea } } });
  const toCarousel = (idea) => navigate("/app/organic/carousel", {
    state: { carouselPrefill: { topic: idea.titolo, pillar: idea.pillar || null, archetype: ARCHETYPE_BY_TIPO[idea.tipo] || "listicle" } },
  });

  if (brain === null) return null;
  const ideas = plan?.output?.ideas || [];
  const pillarCounts = pillars.map((p) => ideas.filter((i) => i.pillar === p).length);
  const unassigned = ideas.filter((i) => !i.pillar || !pillars.includes(i.pillar)).length;

  return (
    <div className="p-6 sm:p-10 max-w-5xl" data-testid="editorial-plan-page">
      <MonoLabel>Organic Studio · Editorial Plan</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Ten ideas, zero blank pages
      </h1>

      <div className="card-soft mt-8 p-6 sm:p-8" data-testid="pillars-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">Content pillars</p>
          <SecondaryButton className="!py-1.5 !px-3 text-[12px]" disabled={proposing || phase === "generating"} onClick={proposePillars} data-testid="pillars-propose-btn">
            <RefreshCw size={12} strokeWidth={1.8} className={proposing ? "animate-spin" : ""} /> {pillars.length ? "Re-propose" : "Propose for me"}
          </SecondaryButton>
        </div>
        <p className="text-[12.5px] text-[#54505B] mt-2">The recurring themes of your content — the 10-idea plan spreads across all of them. Click a pillar to rename it.</p>
        {proposing ? (
          <div className="flex gap-2 mt-4" data-testid="pillars-proposing">
            {[110, 90, 130, 80].map((w, i) => <div key={i} className="h-8 rounded-full bg-[#F3EFE7] animate-pulse" style={{ width: w }} />)}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="pillar-chips">
            {pillars.map((p, i) =>
              editingPillar?.index === i ? (
                <input key={i} autoFocus className="field-input !w-44 !py-1.5 !px-2.5 text-[12.5px]" value={editingPillar.value}
                  onChange={(e) => setEditingPillar({ index: i, value: e.target.value })}
                  onBlur={() => { const v = editingPillar.value.trim(); setEditingPillar(null); if (v && v !== p) savePillars(pillars.map((x, n) => (n === i ? v : x))); }}
                  onKeyDown={(e) => e.key === "Enter" && e.target.blur()} data-testid={`pillar-edit-input-${i}`} />
              ) : (
                <span key={i} className="inline-flex items-center gap-2 rounded-full border border-[#EAE5DB] bg-white px-3 py-1.5 hover:border-[#181621] transition-colors" data-testid={`pillar-chip-${i}`}>
                  <PillarDot color={PILLAR_COLORS[i % PILLAR_COLORS.length]} />
                  <button onClick={() => setEditingPillar({ index: i, value: p })} className="text-[12.5px] font-bold text-[#181621]" data-testid={`pillar-chip-label-${i}`}>{p}</button>
                  {ideas.length > 0 && (
                    <span className="text-[10.5px] font-semibold text-[#9A9591] tabular-nums" data-testid={`pillar-count-${i}`}>{pillarCounts[i]}</span>
                  )}
                  <button onClick={() => savePillars(pillars.filter((_, n) => n !== i))} className="text-[#9A9591] hover:text-[#181621]" data-testid={`pillar-remove-${i}`}>
                    <X size={11} strokeWidth={2.2} />
                  </button>
                </span>
              )
            )}
            {pillars.length < 6 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#E7E2D6] pl-3 pr-1.5 py-1">
                <input className="w-32 bg-transparent outline-none text-[12.5px] text-[#181621] placeholder:text-[#9A9591]" placeholder="Add a pillar…" value={pillarInput}
                  onChange={(e) => setPillarInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && pillarInput.trim()) { savePillars([...pillars, pillarInput.trim()]); setPillarInput(""); } }}
                  data-testid="pillar-add-input" />
                <button disabled={!pillarInput.trim()} onClick={() => { savePillars([...pillars, pillarInput.trim()]); setPillarInput(""); }}
                  className="text-[#54505B] hover:text-[#181621] disabled:opacity-40 p-0.5" data-testid="pillar-add-btn">
                  <Plus size={14} strokeWidth={2} />
                </button>
              </span>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3 mt-6 pt-5 border-t border-[#F3EFE7]">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">This month push… (optional)</label>
            <input className="field-input !py-2" placeholder='e.g. "the grant round opening in September"' value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="focus-input" />
          </div>
          <CtaButton onClick={generatePlan} disabled={phase === "generating" || proposing || !pillars.length} data-testid="generate-plan-btn">
            <Wand2 size={14} strokeWidth={1.8} /> {phase === "generating" ? "Writing…" : "Generate 10 ideas"}
          </CtaButton>
        </div>
        {phase === "error" && <p className="text-[13px] text-[#B4462E] mt-3" data-testid="plan-error">{errMsg}</p>}
      </div>

      {ideas.length > 0 && (
        <div className="mt-10" data-testid="idea-list">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase tabular-nums">Your 10 ideas — {new Date(plan.created_at).toLocaleDateString("en-GB")}</p>
            <p className="text-[12px] text-[#54505B]">Nothing lands on the calendar unless you add it.</p>
          </div>

          <div className="mt-4" data-testid="pillar-distribution">
            <div className="flex h-1.5 rounded-full overflow-hidden bg-[#F3EFE7]">
              {pillars.map((p, i) =>
                pillarCounts[i] > 0 ? (
                  <div key={p} style={{ flex: pillarCounts[i], background: `var(--${PILLAR_COLORS[i % PILLAR_COLORS.length]}-fg)` }} data-testid={`distribution-seg-${i}`} />
                ) : null
              )}
              {unassigned > 0 && <div style={{ flex: unassigned, background: "var(--dot)" }} data-testid="distribution-seg-unassigned" />}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2.5" data-testid="distribution-legend">
              {pillars.map((p, i) => (
                <span key={p} className="inline-flex items-center gap-1.5 text-[11.5px] text-[#54505B]">
                  <PillarDot color={PILLAR_COLORS[i % PILLAR_COLORS.length]} />
                  <span className="font-semibold">{p}</span>
                  <span className="font-bold text-[#181621] tabular-nums">{pillarCounts[i]}</span>
                </span>
              ))}
              {unassigned > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[#9A9591]">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#E7E2D6]" />
                  <span className="font-semibold">Other</span>
                  <span className="font-bold tabular-nums">{unassigned}</span>
                </span>
              )}
            </div>
          </div>

          <div className="mt-5 space-y-4">
            {ideas.map((idea, idx) => {
              const color = pillarColor(idea.pillar, pillars);
              const TipoIcon = TIPO_ICONS[idea.tipo] || BookOpen;
              return (
                <div key={idx} className="card-soft overflow-hidden flex" data-testid={`idea-card-${idx}`}>
                  <div className="w-1.5 shrink-0" style={{ background: `var(--${color}-fg)` }} />
                  <div className="flex-1 min-w-0">
                    <div className="p-5 pb-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-bold text-[#C9C6BE] tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                        {idea.pillar && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EAE5DB] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#181621]" data-testid={`idea-pillar-${idx}`}>
                            <PillarDot color={color} /> {idea.pillar}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#EAE5DB] bg-[#FBFAF7] px-2.5 py-0.5 text-[11px] font-semibold text-[#54505B]">
                          <TipoIcon size={10} strokeWidth={2} /> {idea.tipo || "topic"}
                        </span>
                      </div>
                      <p className="font-bold text-[15px] mt-2.5 text-[#181621]" data-testid={`idea-title-${idx}`}>{idea.titolo}</p>
                      {idea.punti && <p className="text-[12.5px] text-[#54505B] mt-1.5 leading-snug">{idea.punti}</p>}
                      {idea.cta && (
                        <div className="mt-3 rounded-xl border border-[#F3EFE7] bg-[#FBFAF7] px-3.5 py-2.5 flex items-start gap-2.5">
                          <Megaphone size={13} strokeWidth={1.8} className="text-[#E0603A] mt-0.5 shrink-0" />
                          <p className="text-[12.5px] text-[#181621]"><span className="text-[10px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">CTA</span>{idea.cta}</p>
                        </div>
                      )}
                    </div>
                    <div className="px-5 py-3 border-t border-[#F3EFE7] bg-[#FBFAF7]/60 flex gap-2 flex-wrap items-center">
                      <button
                        onClick={() => toScript(idea)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors"
                        data-testid={`idea-script-${idx}`}
                      >
                        <FlaskConical size={12} strokeWidth={1.8} /> Turn into script
                      </button>
                      <button
                        onClick={() => toCarousel(idea)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors"
                        data-testid={`idea-carousel-${idx}`}
                      >
                        <GalleryHorizontalEnd size={12} strokeWidth={1.8} /> Turn into carousel
                      </button>
                      <AddToCalendar added={!!slotFor(idx)} busy={addingIdx === idx} onAdd={(date) => addIdea(idea, idx, date)} testId={`idea-add-${idx}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {ideas.length === 0 && phase !== "generating" && (
        <div className="card-soft mt-8 p-8 text-center text-[13px] text-[#54505B]" data-testid="plan-empty">
          No plan yet — confirm your pillars and generate your first 10 ideas.
        </div>
      )}
      {phase === "generating" && (
        <div className="card-soft mt-8 p-8 text-center" data-testid="plan-generating">
          <p className="font-bold text-[15px]">Writing your 10 ideas…</p>
          <p className="text-[12.5px] text-[#54505B] mt-1">Spread across your pillars, at least one each.</p>
        </div>
      )}
    </div>
  );
}
